import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { api, downloadExport, fmtNum, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import CustomerTable from "@/components/CustomerTable";
import PeriodFilter from "@/components/PeriodFilter";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Search, Download, Filter, ChevronLeft, ChevronRight, X, Trash2, AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";

const LIMIT = 25;

function SelectBox({ value, onChange, options, placeholder, testId }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testId}
      className="h-10 w-full rounded-md border border-input bg-card px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring/40 focus:border-ring sm:w-auto"
    >
      <option value="">{placeholder}</option>
      {options.map((o) => (
        <option key={o} value={o}>{o}</option>
      ))}
    </select>
  );
}

export default function Customers() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [q, setQ] = useState("");
  const [country, setCountry] = useState("");
  const [kyc, setKyc] = useState("");
  const [status, setStatus] = useState("");
  const [accountType, setAccountType] = useState("");
  const [referral, setReferral] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [sortKey, setSortKey] = useState("created");
  const [order, setOrder] = useState("desc");
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(new Set());
  const [confirmSelected, setConfirmSelected] = useState(false);
  const [confirmAll, setConfirmAll] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState({});

  const filters = { q, country, kyc, status, referral, account_type: accountType, date_from: dateFrom, date_to: dateTo };

  const { data: opts } = useQuery({ queryKey: ["filter-options"], queryFn: () => api.get("/filters/options").then((r) => r.data) });
  const { data: refOpts } = useQuery({ queryKey: ["referrals", "", "clients"], queryFn: () => api.get("/referrals").then((r) => r.data) });
  const { data, isLoading } = useQuery({
    queryKey: ["customers", filters, page, sortKey, order],
    queryFn: () => api.get("/customers", { params: { ...filters, page, limit: LIMIT, sort: sortKey, order } }).then((r) => r.data),
  });

  const rows = data?.rows || [];
  const total = data?.total || 0;
  const totalPages = Math.max(1, Math.ceil(total / LIMIT));

  const reset = () => {
    setQ(""); setCountry(""); setKyc(""); setStatus(""); setAccountType(""); setReferral(""); setDateFrom(""); setDateTo(""); setSortKey("created"); setOrder("desc"); setPage(1);
  };
  const onFilter = (setter) => (v) => { setter(v); setPage(1); };

  const onSort = (key) => {
    setPage(1);
    if (sortKey === key) {
      setOrder((o) => (o === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setOrder(key === "created" ? "desc" : "asc");
    }
  };

  const toggle = (id) => setSelected((prev) => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
  const toggleAll = (checked) => setSelected((prev) => {
    const next = new Set(prev);
    rows.forEach((r) => (checked ? next.add(r.id) : next.delete(r.id)));
    return next;
  });

  const refreshAll = () => {
    qc.invalidateQueries({ queryKey: ["customers"] });
    qc.invalidateQueries({ queryKey: ["filter-options"] });
    qc.invalidateQueries({ queryKey: ["overview"] });
    qc.invalidateQueries({ queryKey: ["growth"] });
    qc.invalidateQueries({ queryKey: ["referrals"] });
    qc.invalidateQueries({ queryKey: ["referrals-top"] });
  };

  const deleteSelected = async () => {
    setBusy(true);
    try {
      const { data: res } = await api.post("/customers/delete", { ids: Array.from(selected) });
      toast.success(`Deleted ${res.deleted} record(s)`);
      setSelected(new Set());
      setConfirmSelected(false);
      setPage(1);
      refreshAll();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const deleteAll = async () => {
    setBusy(true);
    try {
      const { data: res } = await api.post("/customers/delete", { all: true });
      toast.success(`Deleted all ${res.deleted} record(s)`);
      setSelected(new Set());
      setConfirmAll(false);
      setConfirmText("");
      setPage(1);
      refreshAll();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const handleExport = async (fmt) => {
    try {
      await downloadExport(filters, fmt);
      toast.success(`Exporting ${total} customers`);
    } catch (e) {
      toast.error("Export failed");
    }
  };

  const openEdit = (c) => {
    setEditForm({
      user_id: c.user_id || "", name: c.name || "", email: c.email || "", country: c.country || "",
      account_type: c.account_type || "individual",
      kyc_status: c.kyc_status || "", referred_by: c.referred_by || "", referral_code: c.referral_code || "",
      status: c.status || "", phone: c.phone || "",
      deposits: c.deposits ?? "", balance: c.balance ?? "",
      created_date: c.created_date ? String(c.created_date).slice(0, 10) : "",
    });
    setEditTarget(c);
  };

  const saveEdit = async () => {
    setBusy(true);
    try {
      const body = { ...editForm };
      body.deposits = body.deposits === "" ? null : parseFloat(body.deposits);
      body.balance = body.balance === "" ? null : parseFloat(body.balance);
      await api.patch(`/customers/${editTarget.id}`, body);
      toast.success("Customer updated");
      setEditTarget(null);
      refreshAll();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const hasFilters = q || country || kyc || status || accountType || referral || dateFrom || dateTo;

  return (
    <div className="space-y-6" data-testid="customers-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-semibold tracking-tight">Customers</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Search and filter your entire client base — <span className="font-medium text-foreground">{fmtNum(total)}</span> records.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => handleExport("csv")} data-testid="export-csv">
            <Download className="mr-2 h-4 w-4" /> CSV
          </Button>
          <Button variant="outline" onClick={() => handleExport("xlsx")} data-testid="export-xlsx">
            <Download className="mr-2 h-4 w-4" /> Excel
          </Button>
          {isAdmin && total > 0 && (
            <Button variant="destructive" onClick={() => { setConfirmText(""); setConfirmAll(true); }} data-testid="delete-all-button">
              <Trash2 className="mr-2 h-4 w-4" /> Delete all
            </Button>
          )}
        </div>
      </div>

      <div className="rounded-md border border-border bg-card p-4">
        <div className="mb-3 flex items-center gap-2 text-sm font-medium text-muted-foreground">
          <Filter className="h-4 w-4" /> Filters
          {hasFilters && (
            <button onClick={reset} className="ml-auto flex items-center gap-1 text-xs text-destructive hover:underline" data-testid="reset-filters">
              <X className="h-3 w-3" /> Reset
            </button>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative w-full sm:min-w-[220px] sm:flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={(e) => onFilter(setQ)(e.target.value)} placeholder="Name, email, user ID, referral..." className="pl-9" data-testid="customer-search" />
          </div>
          <SelectBox value={country} onChange={onFilter(setCountry)} options={opts?.countries || []} placeholder="All Countries" testId="filter-country" />
          <SelectBox
            value={kyc}
            onChange={onFilter(setKyc)}
            options={["__verified__", "__pending__", ...(opts?.kyc_statuses || [])]}
            placeholder="All KYC"
            testId="filter-kyc"
          />
          <select
            value={accountType}
            onChange={(e) => onFilter(setAccountType)(e.target.value)}
            data-testid="filter-account-type"
            className="h-10 w-full rounded-md border border-input bg-card px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring/40 focus:border-ring sm:w-auto"
          >
            <option value="">All Types</option>
            <option value="individual">Individuals</option>
            <option value="company">Corporate</option>
          </select>
          <SelectBox value={status} onChange={onFilter(setStatus)} options={opts?.statuses || []} placeholder="All Status" testId="filter-status" />
          <select
            value={referral}
            onChange={(e) => onFilter(setReferral)(e.target.value)}
            data-testid="filter-referral"
            className="h-10 w-full max-w-[240px] rounded-md border border-input bg-card px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring/40 focus:border-ring sm:w-auto"
          >
            <option value="">All Referrals</option>
            <option value="__none__">— Unassigned (no referral) —</option>
            {(refOpts || []).map((r) => (
              <option key={r.referral} value={r.referral}>{r.name ? `${r.name} (${r.referral})` : r.referral}</option>
            ))}
          </select>
          <select
            value={`${sortKey}:${order}`}
            onChange={(e) => { const [k, o] = e.target.value.split(":"); setSortKey(k); setOrder(o); setPage(1); }}
            data-testid="sort-select"
            className="h-10 w-full rounded-md border border-input bg-card px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring/40 focus:border-ring sm:w-auto"
          >
            <option value="created:desc">Newest first</option>
            <option value="created:asc">Oldest first</option>
            <option value="name:asc">Name A–Z</option>
            <option value="name:desc">Name Z–A</option>
            <option value="email:asc">Email A–Z</option>
            <option value="country:asc">Country A–Z</option>
            <option value="type:asc">Type: Corporate first</option>
            <option value="type:desc">Type: Individual first</option>
            <option value="kyc:desc">KYC: Verified first</option>
            <option value="kyc:asc">KYC: Pending first</option>
            <option value="referred_by:asc">Referred By A–Z</option>
            <option value="deposits:desc">Deposits: high → low</option>
            <option value="deposits:asc">Deposits: low → high</option>
            <option value="balance:desc">Balance: high → low</option>
            <option value="balance:asc">Balance: low → high</option>
          </select>
        </div>
        <div className="mt-3">
          <PeriodFilter
            dateFrom={dateFrom}
            dateTo={dateTo}
            onChange={(f, t) => { setDateFrom(f); setDateTo(t); setPage(1); }}
            testId="customers-period"
          />
        </div>
      </div>

      {isAdmin && selected.size > 0 && (
        <div className="flex items-center justify-between rounded-md border border-[#2b9bb7]/40 bg-[#EDF3F5] px-4 py-2.5" data-testid="selection-bar">
          <span className="text-sm font-medium">{selected.size} selected</span>
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={() => setSelected(new Set())} data-testid="clear-selection">Clear</Button>
            <Button variant="destructive" size="sm" onClick={() => setConfirmSelected(true)} data-testid="delete-selected-button">
              <Trash2 className="mr-2 h-4 w-4" /> Delete selected
            </Button>
          </div>
        </div>
      )}

      <CustomerTable
        rows={rows}
        loading={isLoading}
        selectable={isAdmin}
        selectedIds={selected}
        onToggle={toggle}
        onToggleAll={toggleAll}
        onEdit={isAdmin ? openEdit : undefined}
        sortKey={sortKey}
        order={order}
        onSort={onSort}
        onRowClick={(c) => c.id && navigate(`/customers/${c.id}`)}
      />

      <div className="flex items-center justify-between">
        <span className="text-sm text-muted-foreground">Page {page} of {totalPages}</span>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="prev-page">
            <ChevronLeft className="h-4 w-4" /> Prev
          </Button>
          <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} data-testid="next-page">
            Next <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Delete selected confirm */}
      <Dialog open={confirmSelected} onOpenChange={(o) => !o && setConfirmSelected(false)}>
        <DialogContent data-testid="delete-selected-dialog">
          <DialogHeader>
            <DialogTitle>Delete {selected.size} record(s)?</DialogTitle>
            <DialogDescription>This permanently removes the selected customers. This cannot be undone.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmSelected(false)}>Cancel</Button>
            <Button variant="destructive" onClick={deleteSelected} disabled={busy} data-testid="confirm-delete-selected">
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete all confirm */}
      <Dialog open={confirmAll} onOpenChange={(o) => !o && setConfirmAll(false)}>
        <DialogContent data-testid="delete-all-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-5 w-5" /> Delete ALL customer data
            </DialogTitle>
            <DialogDescription>
              This will permanently delete all <span className="font-semibold text-foreground">{fmtNum(total)}</span> customer records. Type <span className="font-mono font-semibold text-foreground">DELETE</span> to confirm.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Confirmation</Label>
            <Input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder="Type DELETE" data-testid="delete-all-confirm-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmAll(false)}>Cancel</Button>
            <Button variant="destructive" onClick={deleteAll} disabled={busy || confirmText.trim() !== "DELETE"} data-testid="confirm-delete-all">
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Delete everything
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit customer */}
      <Dialog open={!!editTarget} onOpenChange={(o) => !o && setEditTarget(null)}>
        <DialogContent className="max-w-2xl" data-testid="edit-customer-dialog">
          <DialogHeader>
            <DialogTitle>Edit customer</DialogTitle>
            <DialogDescription>Update any field for this client record.</DialogDescription>
          </DialogHeader>
          <div className="grid max-h-[60vh] grid-cols-1 gap-4 overflow-y-auto sm:grid-cols-2">
            {[
              ["user_id", "User ID", "text"], ["name", "Full Name", "text"], ["email", "Email", "text"],
              ["country", "Country", "text"], ["kyc_status", "KYC Status", "text"], ["status", "Account Status", "text"],
              ["referred_by", "Referred By", "text"], ["referral_code", "Referral Code", "text"], ["phone", "Phone", "text"],
              ["deposits", "Deposits", "number"], ["balance", "Balance", "number"], ["created_date", "Created Date", "date"],
            ].map(([key, label, type]) => (
              <div key={key} className="space-y-2">
                <Label className="text-xs">{label}</Label>
                <Input
                  type={type}
                  value={editForm[key] ?? ""}
                  onChange={(e) => setEditForm((f) => ({ ...f, [key]: e.target.value }))}
                  data-testid={`edit-field-${key}`}
                />
              </div>
            ))}
            <div className="space-y-2">
              <Label className="text-xs">Account Type</Label>
              <select
                value={editForm.account_type ?? "individual"}
                onChange={(e) => setEditForm((f) => ({ ...f, account_type: e.target.value }))}
                data-testid="edit-field-account_type"
                className="h-10 w-full rounded-md border border-input bg-card px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring/40 focus:border-ring"
              >
                <option value="individual">Individual</option>
                <option value="company">Corporate</option>
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditTarget(null)}>Cancel</Button>
            <Button onClick={saveEdit} disabled={busy} data-testid="save-customer-edit">
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
